'use client'

import { Button } from "@/components/elements/Button"
import { DateTimePicker } from "@/components/elements/DateTimePicker"
import Input from "@/components/elements/Input"
import { SwitchCard } from "@/components/elements/Switch"
import Textarea from "@/components/elements/Textarea"
import { formatImageURL, formatPrice, getVendorPrimaryColor, resolveColorHex } from "@/utils/util"
import { GIFT_CARD_MIN_AMOUNT, GIFT_CARD_MAX_AMOUNT, GIFT_CARD_PRESETS } from "@/utils/giftCard"
import { useGiftCardForm } from "@/lib/commerce/hooks"
import { motion, AnimatePresence } from "framer-motion"
import { ArrowLeft, ArrowRight, Check, ChevronRight, Gift, Palette, UserPlus, Wallet } from "lucide-react"

function GiftCardPreview({ template, amount, recipient, message, vendorData }: { template: any, amount: string, recipient: string, message: string, vendorData: any }) {
  const value = amount && parseInt(amount, 10) > 0 ? parseInt(amount, 10) : null
  const primaryHex = resolveColorHex(getVendorPrimaryColor(vendorData))
  const thumbnail = template?.thumbnail ? formatImageURL(template.thumbnail) : null

  return (
    <div className="relative aspect-3/2 w-full rounded-2xl overflow-hidden text-white shadow-xl bg-slate-900">
      {thumbnail ? (
        <img src={thumbnail} alt={template?.name || ''} className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${primaryHex}, color-mix(in oklch, ${primaryHex} 55%, white))` }} />
      )}
      <div className="absolute inset-0 bg-black/25" />
      <div className="absolute inset-0 flex flex-col justify-between p-5 sm:p-6">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            {vendorData?.logo && (
              <img src={formatImageURL(vendorData.logo)} alt={vendorData?.name || ''} className="size-9 rounded-lg object-cover bg-white/90 p-0.5 shrink-0" />
            )}
            <div className="min-w-0">
              <div className="text-[10px] font-semibold tracking-[0.22em] uppercase opacity-80">Бэлгийн карт</div>
              <div className="text-[14px] font-bold tracking-[-0.005em] mt-0.5 truncate">{vendorData?.name || 'Shop.mn'}</div>
            </div>
          </div>
          {template?.name && (
            <div className="text-[10px] font-semibold tracking-[0.18em] uppercase opacity-75 shrink-0">{template.name}</div>
          )}
        </div>
        <div className="flex-1 flex items-start min-w-0 pt-2">
          {message ? (
            <div className="text-[11.5px] opacity-90 italic line-clamp-2 leading-relaxed">"{message}"</div>
          ) : (
            <div className="text-[11px] opacity-50">Зурвас бичээгүй</div>
          )}
        </div>
        <div className="flex justify-end">
          {value ? (
            <div className="text-right">
              <div className="text-[36px] sm:text-[44px] font-bold tracking-[-0.02em] leading-none">{formatPrice(value)}</div>
              {recipient && <div className="text-[12px] opacity-80 mt-1.5">{recipient}-д</div>}
            </div>
          ) : (
            <div className="text-[14px] opacity-60">Дүнгээ сонгоно уу</div>
          )}
        </div>
      </div>
    </div>
  )
}

const STEPS = [
  { icon: Palette, label: 'Дизайн' },
  { icon: Wallet, label: 'Үнийн дүн' },
  { icon: UserPlus, label: 'Хүлээн авагч' },
]

export default function GiftCard({ templates, vendorData }: { templates: any, vendorData: any }) {
  const f = useGiftCardForm({ templates, vendorData, totalSteps: STEPS.length })

  const primaryColor = getVendorPrimaryColor(vendorData)
  const primaryHex = resolveColorHex(primaryColor)
  const progress = ((f.step + 1) / STEPS.length) * 100

  return (
    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0, duration: 0.5, ease: "easeIn" }} className="cont py-8 sm:py-8 max-w-7xl">
      <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground mb-6">
        <a href="/" className="hover:text-foreground">Нүүр</a>
        <ChevronRight className="h-3 w-3" />
        <span className="text-foreground font-medium">Бэлгийн карт</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="order-2 lg:order-1 lg:col-span-5 lg:sticky lg:top-6 lg:self-start">
          <GiftCardPreview template={f.selectedTemplate} amount={f.amount} recipient={f.fullName} message={f.message} vendorData={vendorData} />
          <div className="mt-5 rounded-xl border bg-muted/30 p-4">
            <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-muted-foreground mb-2">Захиалга</div>
            <div className="space-y-2 text-[13px]">
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Дизайн</span>
                <span className="font-medium truncate">{f.selectedTemplate?.name || '—'}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Дүн</span>
                <span className="font-medium">{f.amount ? formatPrice(parseInt(f.amount, 10)) : '—'}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-muted-foreground">Хүлээн авагч</span>
                <span className="font-medium truncate">{f.fullName || '—'}</span>
              </div>
              <div className="h-px bg-border my-2" />
              <div className="flex justify-between text-[14px]">
                <span className="font-semibold">Нийт</span>
                <span className="font-bold tabular-nums">{f.amount ? formatPrice(parseInt(f.amount, 10)) : formatPrice(0)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="order-1 lg:order-2 lg:col-span-7">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              {STEPS.map((s, i) => {
                const SI = s.icon
                const done = i < f.step
                const active = i === f.step
                return (
                  <div key={i} className="flex items-center gap-2 flex-1 last:flex-none">
                    <div className={`shrink-0 h-9 w-9 rounded-full grid place-items-center text-[13px] font-semibold transition-colors ${done || active ? 'text-white' : 'bg-muted text-muted-foreground'}`} style={done || active ? { background: primaryHex } : undefined}>
                      {done ? <Check className="h-4 w-4" /> : <SI className="h-4 w-4" />}
                    </div>
                    <div className="hidden sm:block min-w-0">
                      <div className="text-[10.5px] font-semibold tracking-[0.14em] uppercase text-muted-foreground">Алхам {i + 1}</div>
                      <div className="text-[13px] font-semibold leading-tight truncate">{s.label}</div>
                    </div>
                    {i < STEPS.length - 1 && <div className="flex-1 h-px bg-border mx-2" />}
                  </div>
                )
              })}
            </div>
            <div className="h-1 rounded-full bg-muted overflow-hidden">
              <div className="h-full transition-all duration-300" style={{ width: `${progress}%`, background: primaryHex }} />
            </div>
          </div>

          <div className="rounded-2xl border bg-white p-6 sm:p-8 min-h-105">
            <AnimatePresence mode="wait">
              {f.step === 0 && (
                <motion.div key="step-0" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
                  <h3 className="text-[18px] font-semibold mb-1">Картын дизайныг сонгоно уу</h3>
                  <p className="text-[13px] text-muted-foreground mb-5">Зургуудаас тохирохыг сонгоход зүүн талд урьдчилсан байдал шинэчлэгдэнэ.</p>
                  {(() => {
                    const vendorTemplates = (templates || []).filter((t: any) => t.vendorId)
                    const globalTemplates = (templates || []).filter((t: any) => !t.vendorId)
                    const renderTemplate = (template: any, index: number) => {
                      const sel = f.giftCardTemplateId === template.id
                      return (
                        <motion.button key={template.id} type="button" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05, duration: 0.4, ease: "easeIn" }} onClick={() => f.setGiftCardTemplateId(template.id)} className="group flex flex-col gap-2 text-left">
                          <div className="aspect-3/2 w-full overflow-hidden rounded-lg transition-all duration-200" style={sel ? { boxShadow: `0 0 0 2px ${primaryHex}, 0 0 0 4px white` } : undefined}>
                            <img src={formatImageURL(template.thumbnail)} alt={template.name} className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105" />
                          </div>
                          <div className="text-xs text-center font-medium" style={sel ? { color: primaryHex } : undefined}>
                            {template.name}
                          </div>
                        </motion.button>
                      )
                    }
                    const gridClass = (count: number) => `grid gap-3 ${count === 1 ? 'grid-cols-1' : count === 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`
                    return (
                      <div className="space-y-6">
                        {vendorTemplates.length > 0 && (
                          <div>
                            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground mb-3">{vendorData?.name ? `${vendorData.name}-н дизайн` : 'Дэлгүүрийн дизайн'}</div>
                            <div className={gridClass(vendorTemplates.length)}>
                              {vendorTemplates.map((t: any, i: number) => renderTemplate(t, i))}
                            </div>
                          </div>
                        )}
                        {globalTemplates.length > 0 && (
                          <div>
                            {vendorTemplates.length > 0 && (
                              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground mb-3">Бусад дизайн</div>
                            )}
                            <div className={gridClass(globalTemplates.length)}>
                              {globalTemplates.map((t: any, i: number) => renderTemplate(t, i))}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })()}
                </motion.div>
              )}

              {f.step === 1 && (
                <motion.div key="step-1" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
                  <h3 className="text-[18px] font-semibold mb-1">Үнийн дүнг тохируулна уу</h3>
                  <p className="text-[13px] text-muted-foreground mb-5">{formatPrice(GIFT_CARD_MIN_AMOUNT)}-аас {formatPrice(GIFT_CARD_MAX_AMOUNT)}-н хооронд.</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                    {GIFT_CARD_PRESETS.map(p => (
                      <Button key={p} variant={f.amount === String(p) ? "default" : "outline"} onClick={() => f.setAmount(String(p))} className="h-14 text-sm font-semibold w-full" color={vendorData?.color?.primary}>
                        {formatPrice(p)}
                      </Button>
                    ))}
                  </div>
                  <Input placeholder={`Та ${formatPrice(GIFT_CARD_MIN_AMOUNT)}-${formatPrice(GIFT_CARD_MAX_AMOUNT)} үнийн дүнг оруулах боломжтой`} value={f.amount} onChange={(e: any) => f.setAmount(e.target.value)} type="number" description="Гараар мөнгөн дүнг тохируулах" color={vendorData?.color?.primary} />
                </motion.div>
              )}

              {f.step === 2 && (
                <motion.div key="step-2" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }} className="flex flex-col gap-y-4">
                  <div>
                    <h3 className="text-[18px] font-semibold mb-1">Хүлээн авагчийн мэдээлэл</h3>
                    <p className="text-[13px] text-muted-foreground">Картыг өгөгдсөн огноонд автоматаар илгээнэ.</p>
                  </div>
                  {f.isGuest && (
                    <Input label="Өөрийн и-мэйлийг оруулна уу" placeholder="me@mail.mn" value={f.senderEmail} onChange={(e: any) => f.setSenderEmail(e.target.value || f.session?.user?.email || '')} required color={vendorData?.color?.primary} />
                  )}
                  <Input label="Хүлээн авагчийн нэр" placeholder="Хүлээн авагчийн нэр" value={f.fullName} onChange={(e: any) => f.setFullName(e.target.value)} required color={vendorData?.color?.primary} />
                  <Input label="Илгээх хүний и-мэйлийг оруулна уу" placeholder="example@mail.mn" value={f.email} onChange={(e: any) => f.setEmail(e.target.value)} required color={vendorData?.color?.primary} />
                  <Textarea value={f.message} onChange={(e: any) => f.setMessage(e.target.value)} label="Зурвас бичих" placeholder="Сайн уу.." color={vendorData?.color?.primary} />
                  <DateTimePicker date={f.deliveryDate} setDate={(d: Date | undefined) => f.setDeliveryDate(d || null)} label="Илгээх огноо" className="w-full!" color={vendorData?.color?.primary} />
                  <SwitchCard checked={f.sendCopy} onChange={f.setSendCopy} title={'Хуулбар авах'} description={'Бэлгийн картыг илгээх үед таны и-мэйл хаягт картын хуулбар илгээгдэнэ.'} color={vendorData?.color?.primary} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div className="mt-6 flex items-center justify-between gap-3">
            <button type="button" onClick={f.back} disabled={f.step === 0} className="inline-flex items-center gap-2 h-11 px-4 rounded-md border bg-white text-[13.5px] font-medium hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ArrowLeft className="h-4 w-4" />Буцах
            </button>
            {f.step < STEPS.length - 1 ? (
              <Button onClick={f.next} color={vendorData?.color?.primary} className="h-11">
                Үргэлжлүүлэх<ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button onClick={f.submit} isLoading={f.loading} color={vendorData?.color?.primary} className="h-11">
                <Gift className="h-4 w-4" />Захиалах
              </Button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  )
}
